/**
 * Google Analytics 4 – STRIKT Opt-in (DSGVO/TTDSG).
 *
 * Grundsatz: Ohne ausdrückliche Einwilligung wird das gtag.js-Script
 * NICHT geladen und es verlässt kein einziges Byte den Browser Richtung
 * Google — kein Consent-Mode-Ping, kein Cookie. Das ist strenger als
 * Googles "Consent Mode default denied", aber die für Deutschland
 * sichere Variante.
 *
 * Konfiguration: REACT_APP_GA_MEASUREMENT_ID (leer = Analytics komplett
 * aus, z. B. lokal in der Entwicklung).
 *
 * Einwilligung: localStorage 'dc-cookie-consent' = 'granted' | 'denied'.
 * Nicht gesetzt → Banner fragt (CookieConsentBanner.tsx).
 */

export const CONSENT_STORAGE_KEY = 'dc-cookie-consent';

export type ConsentStatus = 'granted' | 'denied' | null;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

type ConsentListener = (status: ConsentStatus) => void;

class AnalyticsService {
  private initialized = false;
  private listeners: ConsentListener[] = [];

  get measurementId(): string {
    return (process.env.REACT_APP_GA_MEASUREMENT_ID || '').trim();
  }

  /** Aktueller Einwilligungs-Stand (null = noch nie gefragt → Banner zeigen) */
  getConsent(): ConsentStatus {
    try {
      const value = localStorage.getItem(CONSENT_STORAGE_KEY);
      return value === 'granted' || value === 'denied' ? value : null;
    } catch {
      // localStorage gesperrt → wie "nie gefragt", aber Banner-Antwort
      // kann dann nicht persistiert werden. Tracking bleibt aus.
      return null;
    }
  }

  /**
   * Einwilligung setzen. 'granted' startet GA sofort (ohne Reload),
   * 'denied' räumt vorhandene GA-Cookies auf (z. B. nach Widerruf
   * über die Cookie-Richtlinien-Seite).
   */
  setConsent(status: 'granted' | 'denied'): void {
    try {
      localStorage.setItem(CONSENT_STORAGE_KEY, status);
    } catch {
      /* nicht persistierbar – Session-weit gilt der Stand trotzdem */
    }
    if (status === 'granted') {
      this.init();
    } else {
      this.removeGaCookies();
    }
    this.listeners.forEach((listener) => listener(status));
  }

  /** Widerruf über die Cookie-Richtlinien-Seite: Banner erneut zeigen */
  resetConsent(): void {
    try {
      localStorage.removeItem(CONSENT_STORAGE_KEY);
    } catch {
      /* noop */
    }
    this.removeGaCookies();
    this.listeners.forEach((listener) => listener(null));
  }

  onConsentChange(listener: ConsentListener): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  /**
   * Lädt gtag.js und konfiguriert GA4. Läuft NUR mit Measurement-ID UND
   * erteilter Einwilligung; beides wird hier (nicht beim Aufrufer) geprüft,
   * damit kein Codepfad versehentlich ohne Consent laden kann.
   */
  init(): void {
    if (this.initialized) return;
    if (!this.measurementId) return;
    if (this.getConsent() !== 'granted') return;

    window.dataLayer = window.dataLayer || [];
    // gtag MUSS über arguments in den dataLayer schreiben (GA-Konvention)
    // eslint-disable-next-line func-names
    window.gtag = function () {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer!.push(arguments);
    };
    window.gtag('js', new Date());
    // send_page_view: false — SPA-Pageviews schickt AnalyticsTracker manuell,
    // sonst zählt GA nur den allerersten Seitenaufruf.
    window.gtag('config', this.measurementId, {
      send_page_view: false,
      anonymize_ip: true,
    });

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(this.measurementId)}`;
    document.head.appendChild(script);

    this.initialized = true;
  }

  isActive(): boolean {
    return this.initialized;
  }

  /** SPA-Pageview (Pfad + Titel). No-op ohne Init/Consent. */
  trackPageView(path: string, title?: string): void {
    if (!this.initialized || typeof window.gtag !== 'function') return;
    window.gtag('event', 'page_view', {
      page_path: path,
      page_location: window.location.href,
      page_title: title ?? document.title,
    });
  }

  /**
   * Eigenes Event (z. B. 'tippspiel_teilnahme', 'scorer_spiel_gestartet').
   * Parameter-Namen: snake_case, max. 40 Zeichen (GA4-Limits).
   */
  trackEvent(name: string, params?: Record<string, string | number | boolean>): void {
    if (!this.initialized || typeof window.gtag !== 'function') return;
    if (!name || typeof name !== 'string') return;
    window.gtag('event', name, params ?? {});
  }

  /** GA-Cookies (_ga, _ga_<container>) für die eigene Domain löschen */
  private removeGaCookies(): void {
    try {
      const cookies = document.cookie ? document.cookie.split(';') : [];
      for (const cookie of cookies) {
        const name = cookie.split('=')[0]?.trim();
        if (!name || !name.startsWith('_ga')) continue;
        const expiry = 'expires=Thu, 01 Jan 1970 00:00:00 GMT';
        const domain = window.location.hostname.replace(/^www\./, '');
        document.cookie = `${name}=; ${expiry}; path=/`;
        document.cookie = `${name}=; ${expiry}; path=/; domain=.${domain}`;
      }
    } catch {
      /* Cookie-Zugriff gesperrt → nichts zu löschen */
    }
  }
}

const analyticsService = new AnalyticsService();
export default analyticsService;
